import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { BridgeWebEventType } from '@pintrack/bridge';
import { bridgeRequest, inNativeShell, onBridgePush } from './bridgeClient';

/**
 * Liga o histórico do React Router ao botão físico de voltar do Android.
 *
 * A casca não sabe navegar — quem navega é o web —, então precisa perguntar. A cada mudança de
 * rota este componente informa se existe para onde voltar; quando o back é pressionado, a casca
 * devolve o aviso e o histórico recua aqui.
 *
 * Sem isto o back sai do app inteiro em vez de recuar uma tela, que é o defeito mais
 * reconhecível de casca WebView e o primeiro que um usuário de Android nota.
 *
 * **Fora da casca não faz nada** e não custa nada: `inNativeShell` é falso no navegador, os dois
 * efeitos saem na primeira linha, e o back do navegador segue sendo o do navegador.
 */
export function NativeNavigationBridge() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!inNativeShell) return;
    void bridgeRequest(BridgeWebEventType.Navigate, {
      path: location.pathname,
      /*
       * `history.length > 1` é aproximado de propósito. O React Router não expõe a
       * profundidade da pilha, e a alternativa — rastrear as entradas por conta própria —
       * erraria de forma pior: um recarregamento zeraria a contagem e o back passaria a fechar
       * o app no meio da navegação. Errar para o lado de "dá para voltar" custa, no pior caso,
       * um toque que não faz nada; errar para o outro lado fecha o app.
       */
      canGoBack: typeof window !== 'undefined' && window.history.length > 1,
    });
  }, [location.pathname]);

  useEffect(() => {
    if (!inNativeShell) return;
    return onBridgePush((payload) => {
      if ((payload as { type?: string } | null)?.type === 'back-pressed') navigate(-1);
    });
  }, [navigate]);

  return null;
}
